import {InjectionToken} from '@angular/core';
import * as Rollbar from 'rollbar';
import {environment} from '../../environments/environment';
import {commitHash} from '../../environments/version';

// Per-session, per-error-signature occurrence counter. Used to cap how many
// times the same error from a single browser session is shipped to Rollbar,
// so a stuck client running an old bundle (or any runaway error loop) can't
// burn through our monthly quota on its own.
const seenErrorCounts = new Map<string, number>();
const PER_ERROR_SESSION_LIMIT = 2;

function errorSignature(payload: any): string {
  // Rollbar payloads carry either a single `trace` or a `trace_chain` (for
  // chained/caused-by errors). Normalise to the first trace.
  const trace = payload?.body?.trace_chain?.[0] ?? payload?.body?.trace;
  const message = trace?.exception?.message ?? trace?.exception?.class ?? '';
  // Top of stack = last frame in Rollbar's frame order.
  const frames = trace?.frames ?? [];
  const top = frames[frames.length - 1] ?? {};
  // Strip the content hash from bundle filenames so the same bug across
  // different deployed bundles collapses into one signature, instead of each
  // hash getting its own fresh 2-event budget.
  const filename = (top.filename ?? '').replace(/\.[a-f0-9]{8,}\.js/, '.js');
  return `${message}|${top.method ?? ''}|${filename}`;
}

export const RollbarConfig = {
  accessToken: '5ebbe233eac347b2807eb98f573ee259',
  captureUncaught: false,
  captureUnhandledRejections: true,
  autoInstrument: {log: false},
  checkIgnore: (_isUncaught: boolean, _args: any, payload: any): boolean => {
    try {
      const sig = errorSignature(payload);
      const next = (seenErrorCounts.get(sig) ?? 0) + 1;
      seenErrorCounts.set(sig, next);
      return next > PER_ERROR_SESSION_LIMIT;
    } catch {
      // Never let the filter itself break Rollbar reporting.
      return false;
    }
  },
  payload: {
    environment: environment.name,
    client: {
      javascript: {
        source_map_enabled: true,
        guess_uncaught_frames: true,
        code_version: commitHash.substr(0, 8),
      }
    }
  }
};

export const RollbarService = new InjectionToken<Rollbar>('rollbar');

export function rollbarFactory(): Rollbar {
  return new Rollbar(RollbarConfig);
}
